export const ONBOARDING_STATUS_VALUES = [
  'onboarding_start',
  'review_phase',
  'rejected',
  'onboarding_complete',
  'bank_details_upload_phase',
  'document_upload_phase',
  'vendor_form_phase',
  'compliance_acknowledgement_phase',
  'collaboration_aggrement_phase',
  'bgv_initiation_phase_1',
  'bgv_initiation_phase_2',
] as const;

/**
 * ONB state string set — the eleven canonical onboarding states.
 * Kept as a narrow type for ONB-specific components (badge colouring,
 * timeline, create-user banner). List/create params accept any
 * string via `CaseStatusValue` so cases in non-ONB workflows can
 * still round-trip a state that isn't in this union.
 */
export type OnboardingStatus = (typeof ONBOARDING_STATUS_VALUES)[number];

/**
 * Any state title returned by `GET /workflows/:abbr/states`. Widen
 * to `string` so a `CR` (or any future workflow) state can populate
 * the list filter without a type-cast at every call site. Runtime
 * validation happens at the workflow-states API boundary.
 */
export type CaseStatusValue = OnboardingStatus | (string & {});

export const ONBOARDING_STATUS_LABELS: Record<OnboardingStatus, string> = {
  onboarding_start: 'Onboarding Start',
  review_phase: 'Review',
  rejected: 'Rejected',
  onboarding_complete: 'Completed',
  bank_details_upload_phase: 'Bank Details Upload',
  document_upload_phase: 'Document Upload',
  vendor_form_phase: 'Vendor Form',
  compliance_acknowledgement_phase: 'Compliance Acknowledgement',
  collaboration_aggrement_phase: 'Collaboration Agreement',
  bgv_initiation_phase_1: 'BGV — Phase 1',
  bgv_initiation_phase_2: 'BGV — Phase 2',
};

export interface CaseContact {
  name: string | null;
  email: string | null;
  phone_number: string | null;
}

export interface CaseJob {
  id: number;
  title: string | null;
  department: string | null;
  program: string | null;
  employment_type: string | null;
  location: string | null;
  status: string | null;
}

/**
 * Bank account row joined into the case detail. Servers decrypt
 * `account_number` for authorized console users; `*_enc` and `*_last4`
 * are always present.
 */
export interface CaseBankAccount {
  name: string | null;
  account_holder_name: string | null;
  account_number_last4: string | null;
  account_number_enc: string | null;
  account_number: string | null;
  ifsc_code: string | null;
  account_type: string | null;
  bank_address: string | null;
}

/**
 * Identity document numbers extracted from the candidate's submitted forms.
 * `*_enc` / `*_last4` are persisted; the decrypted values are only included
 * for authorized roles on the detail endpoint.
 */
export interface CaseDocumentDetails {
  pan_card_number: string | null;
  aadhaar_last4: string | null;
  aadhaar_enc: string | null;
  aadhaar_number: string | null;
}

/**
 * One file uploaded against the case, joined onto the detail response under
 * `attachments[<group>]`. The group key (e.g. `identity`, `bank`,
 * `agreement`, `kyc`) is set server-side based on the document type.
 */
export interface CaseAttachment {
  document_submission_id: number;
  document_version_id: number;
  document_type_id: number;
  document_type_code: string;
  document_type_name: string;
  submission_status: string;
  version_status: string;
  version_number: number;
  mime_type: string;
  file_size_bytes: number;
  uploaded_at: string;
  uploaded_by: string | null;
  uploaded_by_source: string | null;
  rejection_reason: string | null;
  filename: string | null;
}

/**
 * `attachments` is keyed by document category. Known buckets at present:
 * `agreement` (vendor form, compliance acknowledgement, collaboration
 * agreement), `bank` (cancelled cheque), `education` (10th/12th
 * marksheet, graduation degree), `experience` (resume / work history),
 * `identity` (Aadhaar / PAN / photograph), `kyc` (BGV report). Unknown
 * buckets are tolerated so the UI degrades cleanly when backend adds
 * new categories.
 */
export type CaseAttachments = Partial<Record<string, CaseAttachment[]>>;

export interface OnboardingCase {
  id: number;
  created_at: string | null;
  updated_at: string | null;
  state: OnboardingStatus | string;
  current_state_id: number | null;
  workflow_id: number | null;
  initiated_at: string | null;
  completed_at: string | null;
  reporting_manager: string | null;
  /**
   * Display name resolved from the reporting manager's email (e.g.
   * `"Rajinder Kaur"`). Surfaced on the candidate summary alongside the
   * email so reviewers can see the person, not the address.
   */
  reporting_manager_name?: string | null;
  assigned_program_associate: string | null;
  assigned_pnc_officer: string | null;
  rejection_reason: string | null;
  candidate_id: number | null;
  /** Candidate identity fields promoted to the case payload (detail endpoint). */
  date_of_birth?: string | null;
  /**
   * Joining date the candidate agreed to (`YYYY-MM-DD` or full ISO). Same
   * value the user-creation prefill endpoint surfaces as `joining_date`;
   * sourced from `candidate_case` so it's available on the detail GET too.
   */
  joining_date?: string | null;
  gender?: string | null;
  marital_status?: string | null;
  father_or_husband_name?: string | null;
  rgf_id?: string | null;
  /**
   * Permanent address as a single comma-separated string, in the order
   * `Village (CODE), Block, District, PIN`. Parsed into the four named
   * pieces for display via `utils/parseAddress`.
   */
  address?: string | null;
  contact: CaseContact | null;
  job: CaseJob | null;
  /** Primary bank account. Only present on the detail endpoint once submitted. */
  bank_account?: CaseBankAccount | null;
  /** Decrypted identity numbers. Only present on the detail endpoint. */
  document_details?: CaseDocumentDetails | null;
  /** Files grouped by document category. Only present on the detail endpoint. */
  attachments?: CaseAttachments | null;
  /** Server-issued HS256 invite token (24h TTL); only present on the detail endpoint. */
  invite_url_token?: string | null;
}

export type CaseSortBy = 'created_at' | 'updated_at' | 'name';
export type CaseSortOrder = 'asc' | 'desc';

export interface CaseListParams {
  q?: string;
  job_id?: number;
  /**
   * Case status filter — the `title` returned by
   * `GET /workflows/:workflow_abv/states`. Typed as
   * `CaseStatusValue` (which widens to any string) so a `CR` state
   * can round-trip without a cast.
   */
  status?: CaseStatusValue;
  /**
   * Which workflow's cases to fetch. When omitted the server
   * defaults to `ONB` — preserves pre-multi-workflow behaviour for
   * callers that haven't been updated yet.
   */
  workflow_abv?: string;
  /** Exact match (email) on `candidate_case.assigned_pnc_officer`. */
  assigned_pnc_officer?: string;
  /** Exact match (email) on `candidate_case.assigned_program_associate`. */
  assigned_program_associate?: string;
  /** Exact match (email) on `candidate_case.reporting_manager`. */
  reporting_manager?: string;
  sort_by?: CaseSortBy;
  sort_order?: CaseSortOrder;
  page?: number;
  page_size?: number;
}

export interface CaseListPageInfo {
  page: number;
  page_size: number;
  total: number;
  isFirstPage: boolean;
  isLastPage: boolean;
  totalPages: number;
}

export interface CaseListResponse {
  message: string;
  data: {
    list: OnboardingCase[];
    pageInfo: CaseListPageInfo;
  };
}
